import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  FormControl,
  makeStyles,
  Box,
  CircularProgress, Divider,
  Select,
} from "@material-ui/core";
import {
  inputFieldStyles,
  commonInlineStyles,
} from "../../styles";
import { Country, State, City } from "country-state-city";
import postalCodes from "postal-codes-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";
import { useLocation } from "react-router-dom";



const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: "24px",
  },
  formTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 500,
  },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  select: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  button: {
    // minWidth: 180,
    // fontWeight: 600,
    // fontSize: 16,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "14px",
    marginTop: theme.spacing(0.5),
  },
}));

const statusOptions = ["Active", "Inactive"];

// Helper validation functions for the fields
const validateCmoFullName = (value) => {
  // Alphabets, Numbers (0-9), Spaces, Ampersand(&), Hyphen(-), Comma(,), Period (.), Forward slash (/), Apostrophes ('), Parentheses ()
  return /^[a-zA-Z0-9\s&\-,./'()]*$/.test(value);
};

const validateCmoShortName = (value) => {
  // Alphabets, Numbers (0-9), Hyphen(-), Underscore (_)
  return /^[a-zA-Z0-9\-_]*$/.test(value);
};

const validateStreetAddress = (value) => {
  // Alphabets, Digits, spaces, Ampersand(&), point(.), Hyphen(-), Slash(/), Comma(,)
  return /^[a-zA-ZÀ-ÿ0-9\s&.,\-/]*$/.test(value);
};

const validateAdminFullName = (value) => {
  // Alphabets, Spaces
  return /^[a-zA-Z\s]*$/.test(value);
};

const validateAdminEmail = (value) => {
  // Alphabets, Digits, spaces, point(.), Hyphen(-), At symbol(@)
  return /^[a-zA-Z0-9._%+\-@]*$/.test(value) && /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
};

const CMODetails = ({
  formData,
  updateStepData,
  handleNext,
  onValidationChange = () => { },
  onFieldChange,
  readOnly = false, // Add readOnly prop with default value false
  hideStepActions = false,
  hideAdminDetails = false,
  selectedCMOFormData, // Add this prop for fallback data
  originalCmoOrgDetails, // Add this prop for direct API response data
}) => {
  const classes = useStyles(); // Parse phone number into code and number parts if available
  const parsePhoneNumber = (phone) => {
    if (!phone) return { phoneCode: "", phoneNumber: "" };

    // If phone already contains a space, split at the space
    if (phone.includes(" ")) {
      const [code, number] = phone.split(" ", 2);
      return { phoneCode: code, phoneNumber: number };
    }

    // If phone starts with +, assume it's a full number with code
    if (phone.startsWith("+")) {
      // For phone numbers that come from formData.adminDetails.phone
      // We need to return the full phone string as phoneCode for react-phone-input-2
      return { phoneCode: phone, phoneNumber: phone };
    }

    // Default case - just return the phone as the number part
    return { phoneCode: "", phoneNumber: phone };
  };

  // Get the best available data source - prioritize direct API response
  const dataSource = originalCmoOrgDetails?.cmoDetails || selectedCMOFormData?.cmoDetails || formData || {};


  const initialPhone = parsePhoneNumber(
    dataSource?.phoneNumber ||
    formData?.adminPhone ||
    formData?.adminDetails?.phone ||
    formData?.adminDetails?.phoneNumber ||
    formData?.phoneNumber ||
    formData?.cmoDetails?.phoneNumber || ""
  );

  const location = useLocation();
  const existingCmoDetailsReadOnly = location.state?.readOnly;


  const [stepData, setStepData] = useState({
    // Handle both formData structures - either direct stepData format or nested structure
    // Prioritize dataSource (direct API response) over other sources
    cmoFullName:
      dataSource?.cmoName ||
      formData?.cmoFullName ||
      formData?.cmoName ||
      formData?.organizationDetails?.organizationName ||
      formData?.cmoDetails?.cmoName ||
      "",
    cmoShortName:
      dataSource?.cmoShortFormName ||
      formData?.cmoShortName ||
      formData?.cmoShortFormName ||
      formData?.organizationDetails?.orgShortName ||
      formData?.cmoDetails?.cmoShortFormName ||
      "",
    streetAddress1:
      dataSource?.streetAddress1 ||
      formData?.streetAddress1 ||
      formData?.organizationDetails?.streetAddress1 ||
      formData?.cmoDetails?.streetAddress1 ||
      "",
    streetAddress2:
      dataSource?.streetAddress2 ||
      formData?.streetAddress2 ||
      formData?.organizationDetails?.streetAddress2 ||
      formData?.cmoDetails?.streetAddress2 ||
      "",
    city: dataSource?.city || formData?.city || formData?.organizationDetails?.city || formData?.cmoDetails?.city || "",
    state: dataSource?.state || formData?.state || formData?.organizationDetails?.state || formData?.cmoDetails?.state || "",
    postCode:
      dataSource?.postCode || formData?.postCode || formData?.organizationDetails?.postCode || formData?.cmoDetails?.postCode || "",
    country: dataSource?.country || formData?.country || formData?.organizationDetails?.country || formData?.cmoDetails?.country || "",
    status: formData?.status || "Active",
    adminFullName:
      dataSource?.fullName ||
      formData?.fullName || formData?.adminFullName || formData?.adminDetails?.fullName || formData?.cmoDetails?.fullName || "",
    adminEmail:
      dataSource?.emailAddress ||
      formData?.emailAddress ||
      formData?.adminEmail ||
      formData?.adminDetails?.email ||
      formData?.adminDetails?.emailAddress ||
      formData?.cmoDetails?.emailAddress || "",
    adminPhoneCode: initialPhone.phoneCode,
    adminPhoneNumber: initialPhone.phoneNumber,
    ecosystemId: formData?.ecosystemId || localStorage.ecosystemId,
    // Include any additional properties from cmoDetails if they exist
    ...Object.fromEntries(
      Object.entries(formData?.cmoDetails || {}).map(([k, v]) => [k, v ?? ""])
    ),
  });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [postalCodeError, setPostalCodeError] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [brandOwnerMosymphonyID, setBrandOwnerMosymphonyID] = useState(null);

  // Add state to track changes for update scenario
  const [originalData, setOriginalData] = useState(null);
  const [cmoDetailsChanged, setCmoDetailsChanged] = useState(false);
  const [adminDetailsChanged, setAdminDetailsChanged] = useState(false);
  const API_BASE_URL = useApiBaseUrl(appUrl);


  useEffect(() => {

    // Skip updating if in readonly mode
    if (readOnly) return;

    // Only update if formData is provided and different from current stepData
    if ((formData || dataSource) && JSON.stringify(stepData) !== JSON.stringify(formData)) {
      // Parse phone number if it exists - handle both structure formats
      const phone = dataSource?.phoneNumber ||
        formData?.adminPhone ||
        formData?.adminDetails?.phone ||
        formData?.adminDetails?.phoneNumber ||
        formData?.phoneNumber ||
        formData?.cmoDetails?.phoneNumber || "";
      const parsedPhone = parsePhoneNumber(phone);


      // Handle both formData structures - direct stepData format or nested structure
      // Prioritize dataSource (direct API response) over other sources
      const newStepData = {
        cmoFullName:
          dataSource?.cmoName ||
          formData?.cmoFullName ||
          formData?.cmoName ||
          formData?.organizationDetails?.organizationName ||
          formData?.cmoDetails?.cmoName ||
          stepData.cmoFullName,
        cmoShortName:
          dataSource?.cmoShortFormName ||
          formData?.cmoShortName ||
          formData?.cmoShortFormName ||
          formData?.organizationDetails?.orgShortName ||
          formData?.cmoDetails?.cmoShortFormName ||
          stepData.cmoShortName,
        streetAddress1:
          dataSource?.streetAddress1 ||
          formData?.streetAddress1 ||
          formData?.organizationDetails?.streetAddress1 ||
          formData?.cmoDetails?.streetAddress1 ||
          stepData.streetAddress1,
        streetAddress2:
          dataSource?.streetAddress2 ||
          formData?.streetAddress2 ||
          formData?.organizationDetails?.streetAddress2 ||
          formData?.cmoDetails?.streetAddress2 ||
          stepData.streetAddress2,
        city:
          dataSource?.city ||
          formData?.city ||
          formData?.organizationDetails?.city ||
          formData?.cmoDetails?.city ||
          stepData.city,
        state:
          dataSource?.state ||
          formData?.state ||
          formData?.organizationDetails?.state ||
          formData?.cmoDetails?.state ||
          stepData.state,
        postCode:
          dataSource?.postCode ||
          formData?.postCode ||
          formData?.organizationDetails?.postCode ||
          formData?.cmoDetails?.postCode ||
          stepData.postCode,
        country:
          dataSource?.country ||
          formData?.country ||
          formData?.organizationDetails?.country ||
          formData?.cmoDetails?.country ||
          stepData.country,
        status: formData?.status || stepData.status || "Active",
        adminFullName:
          dataSource?.fullName ||
          formData?.fullName ||
          stepData?.fullName ||
          formData?.adminDetails?.fullName ||
          formData?.cmoDetails?.fullName ||
          stepData.adminFullName,
        adminEmail:
          dataSource?.emailAddress ||
          formData?.emailAddress ||
          stepData?.emailAddress ||
          formData?.adminDetails?.email ||
          formData?.adminDetails?.emailAddress ||
          formData?.cmoDetails?.emailAddress ||
          stepData.adminEmail,
        adminPhoneCode: parsedPhone.phoneCode || stepData.adminPhoneCode,
        adminPhoneNumber: parsedPhone.phoneNumber || formData?.phoneNumber || stepData?.phoneNumber,
        ecosystemId: formData?.ecosystemId || stepData.ecosystemId,
      };

      setStepData(newStepData);

      // Store original data for change detection if not already set
      if (!originalData) {
        setOriginalData(newStepData);
      }

      // Validate postal code when form data is loaded
      if (newStepData.country && newStepData.postCode) {
        const result = postalCodes.validate(
          newStepData.country,
          newStepData.postCode
        );
        setPostalCodeError(result === true ? "" : result);
      }
    }
    // eslint-disable-next-line
  }, [formData, readOnly, dataSource]);

  // Effect to detect changes for update scenario
  useEffect(() => {
    if (originalData && stepData) {
      // Check if CMO details have changed
      const cmoFieldsChanged =
        originalData.cmoFullName !== stepData.cmoFullName ||
        originalData.cmoShortName !== stepData.cmoShortName ||
        originalData.streetAddress1 !== stepData.streetAddress1 ||
        originalData.streetAddress2 !== stepData.streetAddress2 ||
        originalData.city !== stepData.city ||
        originalData.state !== stepData.state ||
        originalData.postCode !== stepData.postCode ||
        originalData.country !== stepData.country ||
        originalData.status !== stepData.status;

      // Check if admin details have changed
      const adminFieldsChanged =
        originalData.adminFullName !== stepData.adminFullName ||
        originalData.adminEmail !== stepData.adminEmail ||
        originalData.adminPhoneCode !== stepData.adminPhoneCode ||
        originalData.adminPhoneNumber !== stepData.adminPhoneNumber;

      setCmoDetailsChanged(cmoFieldsChanged);
      setAdminDetailsChanged(adminFieldsChanged);
    }
  }, [stepData, originalData]);

  useEffect(() => {
    // Skip API calls in readOnly mode
    if (readOnly) return;

    if (stepData.country) {
      setStates(State.getStatesOfCountry(stepData.country));

      // Removed automatic phone code selection when country changes
    } else {
      setStates([]);
    }

    // If we get country as name instead of code, try to convert it
    if (stepData.country && !Country.getCountryByCode(stepData.country)) {
      const countryObj = Country.getAllCountries().find(
        (c) => c.name.toLowerCase() === String(stepData.country).toLowerCase()
      );
      if (countryObj) {
        setStepData((prev) => ({ ...prev, country: countryObj.isoCode }));
      }
    }
  }, [stepData.country]);

  useEffect(() => {
    if (stepData.country && stepData.state) {
      setCities(City.getCitiesOfState(stepData.country, stepData.state));
    } else {
      setCities([]);
    }

    // If we get state as name instead of code, try to convert it
    if (
      stepData.country &&
      stepData.state &&
      !State.getStateByCodeAndCountry(stepData.state, stepData.country)
    ) {
      const stateObj = State.getStatesOfCountry(stepData.country).find(
        (s) => s.name.toLowerCase() === String(stepData.state).toLowerCase()
      );
      if (stateObj) {
        setStepData((prev) => ({ ...prev, state: stateObj.isoCode }));
      }
    }
  }, [stepData.country, stepData.state]);
  // Add an effect to validate postal code when country or postal code changes
  useEffect(() => {
    // Skip validation in readOnly mode
    if (readOnly) return;

    if (stepData.country && stepData.postCode) {
      const result = postalCodes.validate(stepData.country, stepData.postCode);
      setPostalCodeError(result === true ? "" : result);
    }
  }, [stepData.country, stepData.postCode, readOnly]);  // Validation logic

  const token = localStorage.getItem('authToken');


  useEffect(() => {

    if (token) {
      try {
        const base64Url = token.split('.')[1]; // Get payload
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
            .join('')
        );

        const payload = JSON.parse(jsonPayload);
        setBrandOwnerMosymphonyID(payload.organization_id)

      } catch (e) {
        console.error('Invalid token', e);
      }
    }
  }, [token]);

  useEffect(() => {
    if (existingCmoDetailsReadOnly && location.state?.cmoDetails) {
      // setStepData(location.state);
      if (typeof updateStepData === 'function') {
        updateStepData(location.state.cmoDetails);
      } else {
        // If updateStepData is not a function, update the state directly
        setStepData(prevState => ({
          ...prevState,
          cmoFullName: location.state?.cmoDetails?.organizationDetails?.organizationName || prevState.cmoFullName,
          cmoShortName: location.state?.cmoDetails?.organizationDetails?.orgShortName || prevState.cmoShortName,
          streetAddress1: location.state?.cmoDetails?.organizationDetails?.streetAddress1 || prevState.streetAddress1,
          streetAddress2: location.state?.cmoDetails?.organizationDetails?.streetAddress2 || prevState.streetAddress2,
          city: location.state?.cmoDetails?.organizationDetails?.city || prevState.city,
          state: location.state?.cmoDetails?.organizationDetails?.state || prevState.state,
          postCode: location.state?.cmoDetails?.organizationDetails?.postCode || prevState.postCode,
          country: location.state?.cmoDetails?.organizationDetails?.country || prevState.country,
        }));
      }
    }
  }, [existingCmoDetailsReadOnly, location.state]);



  const validate = () => {
    // Skip validation in readOnly mode
    if (readOnly) return true;

    let isValid = true;
    const newErrors = {};

    // Required fields validation
    if (!stepData.cmoFullName?.trim()) {
      newErrors.cmoFullName = "CMO Full Name is required";
      isValid = false;
    } else if (!validateCmoFullName(stepData.cmoFullName)) {
      newErrors.cmoFullName = "Only allows Alphabets, Numbers (0-9), Spaces, Ampersand(&), Hyphen(-), Comma(,), Period (.), Forward slash (/), Apostrophes ('), Parentheses ()";
      isValid = false;
    } else if (stepData.cmoFullName.length > 100) {
      newErrors.cmoFullName = "Maximum 100 characters allowed";
      isValid = false;
    }

    if (!stepData.cmoShortName?.trim()) {
      newErrors.cmoShortName = "CMO Short Name is required";
      isValid = false;
    } else if (!validateCmoShortName(stepData.cmoShortName)) {
      newErrors.cmoShortName = "Only allows Alphabets, Numbers (0-9), Hyphen(-), Underscore (_)";
      isValid = false;
    } else if (stepData.cmoShortName.length > 20) {
      newErrors.cmoShortName = "Maximum 20 characters allowed";
      isValid = false;
    }

    if (!stepData.streetAddress1?.trim()) {
      newErrors.streetAddress1 = "Street Address 1 is required";
      isValid = false;
    } else if (!validateStreetAddress(stepData.streetAddress1)) {
      newErrors.streetAddress1 = "Only allows Alphabets, Digits, spaces, Ampersand(&), point(.), Hyphen(-), Slash(/), Comma(,)";
      isValid = false;
    } else if (stepData.streetAddress1.length > 255) {
      newErrors.streetAddress1 = "Maximum 255 characters allowed";
      isValid = false;
    }

    if (!stepData.streetAddress2?.trim()) {
      newErrors.streetAddress2 = "Street Address 2 is required";
      isValid = false;
    } else if (!validateStreetAddress(stepData.streetAddress2)) {
      newErrors.streetAddress2 = "Only allows Alphabets, Digits, spaces, Ampersand(&), point(.), Hyphen(-), Slash(/), Comma(,)";
      isValid = false;
    } else if (stepData.streetAddress2.length > 255) {
      newErrors.streetAddress2 = "Maximum 255 characters allowed";
      isValid = false;
    }

    if (!stepData.country) {
      newErrors.country = "Country is required";
      isValid = false;
    }
    if (!stepData.state) {
      newErrors.state = "State is required";
      isValid = false;
    }
    if (!stepData.city) {
      newErrors.city = "City is required";
      isValid = false;
    }

    if (!stepData.postCode?.trim()) {
      newErrors.postCode = "Post Code is required";
      isValid = false;
    } else if (stepData.country && postalCodeError) {
      // Add validation check for postalCodeError state
      newErrors.postCode = postalCodeError;
      isValid = false;
    }

    if (!stepData.status) {
      newErrors.status = "Status is required";
      isValid = false;
    }

    if (!stepData.adminFullName?.trim()) {
      newErrors.adminFullName = "Admin Full Name is required";
      isValid = false;
    } else if (!validateAdminFullName(stepData.adminFullName)) {
      newErrors.adminFullName = "Only allows Alphabets and Spaces";
      isValid = false;
    } else if (stepData.adminFullName.length > 30) {
      newErrors.adminFullName = "Maximum 30 characters allowed";
      isValid = false;
    }

    if (!stepData.adminEmail?.trim()) {
      newErrors.adminEmail = "Admin Email is required";
      isValid = false;
    } else if (!validateAdminEmail(stepData.adminEmail)) {
      newErrors.adminEmail = "Invalid email address. Only allows Alphabets, Digits, spaces, point(.), Hyphen(-), At symbol(@)";
      isValid = false;
    } else if (stepData.adminEmail.length > 320) {
      newErrors.adminEmail = "Maximum 320 characters allowed";
      isValid = false;
    }

    if (!stepData.adminPhoneCode) {
      newErrors.adminPhone = "Admin Phone is required";
      isValid = false;
    } else if (!stepData.adminPhoneCode.startsWith('+') && stepData.adminPhoneCode.length < 8) {
      // Basic validation for phone numbers - most international numbers are at least 8 digits
      newErrors.adminPhone = "Please enter a valid phone number";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Call validation on every stepData change
  // useEffect(() => {
  //   onValidationChange(validate());
  // }, [stepData]);
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setStepData((prev) => ({ ...prev, [name]: value }));

    // Call parent's onFieldChange function if it exists
    if (typeof onFieldChange === 'function') {
      onFieldChange(name, value);
    }

    // Reset dependent fields when country or state changes
    if (name === "country") {
      // Find the country info
      const selectedCountry = Country.getCountryByCode(value);

      // Reset state and city when country changes
      if (selectedCountry) {
        setStepData((prev) => ({
          ...prev,
          state: "",
          city: ""
        }));

        // Revalidate postal code when country changes
        if (stepData.postCode) {
          const result = postalCodes.validate(value, stepData.postCode);
          setPostalCodeError(result === true ? "" : result);
        }
      } else {
        setStepData((prev) => ({ ...prev, state: "", city: "" }));
      }
    }
    if (name === "state") {
      setStepData((prev) => ({ ...prev, city: "" }));
    }

    // Clear validation errors when a field is changed
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };
  const handlePostalCodeChange = (e) => {
    // Apply consistent formatting to match the SponsorDetails component
    // Only allow alphanumeric characters, spaces and hyphens
    // Replace multiple consecutive spaces or hyphens with a single one
    let value = e.target.value
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .replace(/(\s{2,}|-{2,})/g, (match) => match[0]);

    // Limit to 12 characters (standard for most postal codes worldwide)
    if (value.length > 12) {
      value = value.substring(0, 12);
    }

    setStepData((prev) => ({ ...prev, postCode: value }));

    // Call parent's onFieldChange function if it exists
    if (typeof onFieldChange === 'function') {
      onFieldChange('postCode', value);
    }

    // Clear postCode error if it exists
    if (errors.postCode) {
      setErrors((prev) => ({ ...prev, postCode: "" }));
    }

    // Validate against country-specific format if country is selected
    if (stepData.country) {
      const result = postalCodes.validate(stepData.country, value);
      setPostalCodeError(result === true ? "" : result);
    }
  };  // Handle PhoneInput component changes
  const handlePhoneCodeChange = (phone, countryData) => {
    // Allow clearing the phone field completely
    const phoneValue = phone || "";

    setStepData((prev) => ({
      ...prev,
      adminPhoneCode: phoneValue,
      // We're using the same value for both fields since react-phone-input-2 handles the complete phone number
      adminPhoneNumber: phoneValue
    }));

    // Call parent's onFieldChange function if it exists
    if (typeof onFieldChange === 'function') {
      onFieldChange('adminPhoneCode', phoneValue);
      onFieldChange('adminPhoneNumber', phoneValue);
    }

    if (errors.adminPhone) {
      setErrors((prev) => ({ ...prev, adminPhone: "" }));
    }
  };

  // Add a specific handler for phone input keydown to detect backspace and delete
  const handlePhoneKeyDown = (e) => {
    // Check if the key pressed is backspace or delete and the field is empty or all content is selected
    if ((e.key === 'Backspace' || e.key === 'Delete') &&
      (e.target.value === '' ||
        (e.target.selectionStart === 0 && e.target.selectionEnd === e.target.value.length))) {
      // Clear the phone fields
      setStepData(prev => ({
        ...prev,
        adminPhoneCode: '',
        adminPhoneNumber: ''
      }));

      // Call parent's onFieldChange function if it exists
      if (typeof onFieldChange === 'function') {
        onFieldChange('adminPhoneCode', '');
        onFieldChange('adminPhoneNumber', '');
      }
    }
  };

  // Handle phone number input changes
  const handlePhoneNumberChange = (e) => {
    // Get the raw value with only digits
    let value = e.target.value.replace(/\D/g, "");

    // Check for country-specific formatting
    if (stepData.country) {
      const countryCode = stepData.country;

      // Apply country-specific validation and formatting
      switch (countryCode) {
        case "US":
        case "CA":
          // North American format: xxx-xxx-xxxx (10 digits)
          if (value.length > 10) {
            value = value.slice(0, 10);
          }
          break;
        case "IN":
          // India: 10 digits
          if (value.length > 10) {
            value = value.slice(0, 10);
          }
          break;
        case "GB":
          // UK: variable length but generally up to 11 digits
          if (value.length > 11) {
            value = value.slice(0, 11);
          }
          break;
        default:
          // Default max length of 15 digits (ITU-T E.164 standard)
          if (value.length > 15) {
            value = value.slice(0, 15);
          }
      }
    } else {
      // If no country selected, use a reasonable default max length
      if (value.length > 15) {
        value = value.slice(0, 15);
      }
    }

    setStepData((prev) => ({ ...prev, adminPhoneNumber: value }));

    // Call parent's onFieldChange function if it exists
    if (typeof onFieldChange === 'function') {
      onFieldChange('adminPhoneNumber', value);
    }
  };
  const handleNextClick = async () => {
    // In readonly mode, just proceed to next step without validation
    if (readOnly) {
      handleNext();
      return;
    }

  

    setSubmitted(true);
    if (validate()) {
      setLoading(true);
      const countryData = Country.getCountryByCode(stepData.country);
      const stateData = State.getStateByCodeAndCountry(
        stepData.state,
        stepData.country
      );
      // Format phone number from the PhoneInput - it should already be correct
      const formatPhoneNumber = (phoneCode) => {
        // Make sure phoneCode starts with +
        return phoneCode.startsWith("+") ? phoneCode : `+${phoneCode}`;
      };

      const formattedPhone = formatPhoneNumber(stepData.adminPhoneCode);

      // Check if this is an update scenario (cmoDetailsResponse exists in sessionStorage)
      const cmoDetailsResponse = JSON.parse(sessionStorage.getItem("cmoDetailsResponse"));

      if (cmoDetailsResponse) {
        // This is an update scenario - call updateVendorPlantDetails API
        try {
          const payload = {
            vendorMosymphonyID: cmoDetailsResponse?.organizationId,
            brandOwnerMosymphonyID: brandOwnerMosymphonyID,
          };

          // Add CMO organization details if changed
          if (cmoDetailsChanged) {
            payload.cmoOrgDetails = {
              organizationName: stepData.cmoFullName,
              orgShortName: stepData.cmoShortName,
              streetAddress1: stepData.streetAddress1,
              streetAddress2: stepData.streetAddress2,
              city: stepData.city,
              state: stateData?.name || stepData.state,
              country: countryData?.name || stepData.country,
              postCode: stepData.postCode,
            };
            payload.updateCmoOrgDetailsFlag = true;
          }

          // Add admin details if changed
          if (adminDetailsChanged) {
            payload.cmoAdminDetails = {
              fullName: stepData.adminFullName,
              phoneNumber: formattedPhone,
              emailAddress: stepData.adminEmail,

            };
            payload.updateCmoAdminDetailsFlag = true;
          }

          // Only make API call if there are changes
          if (cmoDetailsChanged || adminDetailsChanged) {
            if (!API_BASE_URL) {
              console.error("API base URL is not available");
              setLoading(false);
              return;
            }

            const response = await axios.post(`${API_BASE_URL}/updateVendorPlantDetails`, payload)

            if (response.data && response.data.status) {
              // Update the step data with the response
              updateStepData({
                ...formData,
                status: stepData.status,
                organizationDetails: payload.cmoOrgDetails,
                adminDetails: payload.cmoAdminDetails,
              });

              // Update sessionStorage cmoDetailsResponse with new admin details
              const updatedCmoDetailsResponse = {
                ...cmoDetailsResponse,
                ...(adminDetailsChanged && {
                  email: stepData.adminEmail,
                  fullName: stepData.adminFullName,
                  phoneNumber: formattedPhone
                })
              };
              
              sessionStorage.setItem("cmoDetailsResponse", JSON.stringify(updatedCmoDetailsResponse));
              
            } else {
              console.error("Update API call failed:", response.data);
              // You might want to show an error message to the user here
            }
          } else {
            console.error("No changes detected, skipping update API call");
          }

          // Proceed to next step
          handleNext();
        } catch (error) {
          console.error('Error updating CMO network details:', error);
          // You might want to show an error message to the user here
        } finally {
          setLoading(false);
        }
      } else {
        // This is initial creation scenario - call addCmoNetworkDetails API
        try {
          let payload = {};
          

          // Create both data structures for compatibility
          payload = {
            status: stepData.status,
            // updateStatusFlag: true,
            // ecosystemId: stepData.ecosystemId,

            // Nested structure for API integration
            organizationDetails: {
              organizationName: stepData.cmoFullName,
              orgShortName: stepData.cmoShortName,
              streetAddress1: stepData.streetAddress1,
              streetAddress2: stepData.streetAddress2,
              city: stepData.city,
              state: stateData?.name || stepData.state,
              country: countryData?.name || stepData.country,
              postCode: stepData.postCode,
            },
            adminDetails: {
              fullName: stepData.adminFullName,
              phone: formattedPhone,
              email: stepData.adminEmail,
              collaboratorRoleId: 2,
              collaboratorRoleName: "Admin",
              collaboratorTypeId: 2,
            },
          };

          if (existingCmoDetailsReadOnly && location.state?.cmoDetails?.organizationDetails) {
            payload = {
              status: stepData.status,
              organizationDetails: location.state.cmoDetails.organizationDetails,
              adminDetails: {
                fullName: stepData.adminFullName,
                phone: formattedPhone,
                email: stepData.adminEmail,
                collaboratorRoleId: 2,
                collaboratorRoleName: "Admin",
                collaboratorTypeId: 2,
              },
            };
          } 

         



          // Call the API to add CMO network details
          // Use appUrl directly for this endpoint as it has a different path structure
          const response = await axios.post(
            `${appUrl}/user/addCmoNetworkDetails`,
            payload
          );

          if (response.data && response.data.status) {
            // Update the step data with the response
            updateStepData(payload);
            sessionStorage.setItem("cmoDetailsResponse", JSON.stringify(response.data.data));
            // Proceed to next step after successful API call
            handleNext();
          } else {
            console.error("API call failed:", response.data);
            
            // Check if it's a duplicate organization name error
            if (response.data && response.data.errorMessage && 
                response.data.errorMessage.includes("organization with this name already exists")) {
              // Set error for CMO Full Name field
              setErrors(prev => ({
                ...prev,
                cmoFullName: "Organization with this name already exists. Please choose a different name."
              }));
              
              // Ensure submitted state remains true to show the error
              setSubmitted(true);
              
              // Scroll to the CMO Full Name field
              setTimeout(() => {
                const cmoFullNameField = document.getElementById("cmoFullName");
                if (cmoFullNameField) {
                  cmoFullNameField.scrollIntoView({ 
                    behavior: "smooth", 
                    block: "center" 
                  });
                  cmoFullNameField.focus();
                }
              }, 100);
            } else if (response.data && response.data.errorMessage && 
                       response.data.errorMessage.includes("No available CMO slots in this ecosystem")) {
              // Set error for ecosystem limit
              setErrors(prev => ({
                ...prev,
                cmoFullName: "No available CMO slots in this ecosystem. Please contact your administrator."
              }));
              
              // Ensure submitted state remains true to show the error
              setSubmitted(true);
              
              // Scroll to the CMO Full Name field
              setTimeout(() => {
                const cmoFullNameField = document.getElementById("cmoFullName");
                if (cmoFullNameField) {
                  cmoFullNameField.scrollIntoView({ 
                    behavior: "smooth", 
                    block: "center" 
                  });
                  cmoFullNameField.focus();
                }
              }, 100);
            } else {
              // Handle other API errors
              console.error("API call failed:", response.data);
              // You might want to show a general error message to the user here
            }
          }
        } catch (error) {
          console.error('Error adding CMO network details:', error);
          
          // Helper function to check for ecosystem slot error in any response structure
          const isEcosystemSlotError = (errorData) => {
            if (!errorData) return false;
            
            // Check various possible locations for the error message
            const possibleMessages = [
              errorData.errorMessage,
              errorData.message,
              errorData.error,
              errorData.data?.errorMessage,
              errorData.data?.message,
              JSON.stringify(errorData)
            ].filter(Boolean);
            
            return possibleMessages.some(msg => 
              String(msg).toLowerCase().includes("no available cmo slots in this ecosystem")
            );
          };
          
          // Helper function to check for duplicate name error
          const isDuplicateNameError = (errorData) => {
            if (!errorData) return false;
            
            const possibleMessages = [
              errorData.errorMessage,
              errorData.message,
              errorData.error,
              errorData.data?.errorMessage,
              errorData.data?.message,
              JSON.stringify(errorData)
            ].filter(Boolean);
            
            return possibleMessages.some(msg => 
              String(msg).toLowerCase().includes("organization with this name already exists")
            );
          };
          
          // Check error response data
          const errorData = error.response?.data || error.message || error;
          
          if (isDuplicateNameError(errorData)) {
            // Set error for CMO Full Name field
            setErrors(prev => ({
              ...prev,
              cmoFullName: "Organization with this name already exists. Please choose a different name."
            }));
            
            // Ensure submitted state remains true to show the error
            setSubmitted(true);
            
            // Scroll to the CMO Full Name field
            setTimeout(() => {
              const cmoFullNameField = document.getElementById("cmoFullName");
              if (cmoFullNameField) {
                cmoFullNameField.scrollIntoView({ 
                  behavior: "smooth", 
                  block: "center" 
                });
                cmoFullNameField.focus();
              }
            }, 100);
          } else if (isEcosystemSlotError(errorData)) {
            // Set error for ecosystem limit
            setErrors(prev => ({
              ...prev,
              cmoFullName: "No available CMO slots in this ecosystem. Please contact your administrator."
            }));
            
            // Ensure submitted state remains true to show the error
            setSubmitted(true);
            
            // Scroll to the CMO Full Name field
            setTimeout(() => {
              const cmoFullNameField = document.getElementById("cmoFullName");
              if (cmoFullNameField) {
                cmoFullNameField.scrollIntoView({ 
                  behavior: "smooth", 
                  block: "center" 
                });
                cmoFullNameField.focus();
              }
            }, 100);
          } else {
            // Handle other network/API errors
            console.error('Network error adding CMO network details:', error);
            console.error('Error response data:', errorData);
            // You might want to show a general error message to the user here
          }
        } finally {
          setLoading(false);
        }
      }
    } else {
      setLoading(false);
    }
  };




  return (
    <div className="cmo-stepper-wrapper cmo-details-wrapper">
      <div className="stepper-container">
        <form>
          <Typography variant="h6" className={classes.formTitle}>
            CMO Details
          </Typography>
          <Grid className="two-col-forms-grid" container spacing={3}>
            {/* CMO Full Name */}
            <Grid item xs={12} sm={6}>
              <FormControl className={classes?.formField}>
                <Typography component="label" htmlFor="cmoFullName">
                  CMO Full Name {!readOnly && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {(readOnly || hideStepActions || existingCmoDetailsReadOnly) ? (
                  <Typography>
                    {dataSource?.cmoName || formData?.cmoFullName || formData?.cmoDetails?.organizationDetails?.organizationName || formData?.cmoDetails?.organizationDetails?.cmoName || stepData?.cmoFullName || "N/A"}
                  </Typography>
                ) : (<TextField
                  id="cmoFullName"
                  name="cmoFullName"
                  value={stepData.cmoFullName}
                    onChange={handleFieldChange}
                    onBlur={(e) => {
                      const { value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        handleFieldChange({ target: { name: "cmoFullName", value: value.trim() } });
                      }
                    }}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  required
                  error={submitted && Boolean(errors.cmoFullName)}
                  helperText={submitted ? errors.cmoFullName : ""}
                  InputProps={{ style: commonInlineStyles.input }}
                  sx={inputFieldStyles}
                  inputProps={{
                    ...commonInlineStyles.inputProps,
                    maxLength: 100,
                  }}
                  style={commonInlineStyles.input}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^a-zA-Z0-9\s&\-,./'()]/g, "")
                      .replace(/(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g, (match) => match[0]);
                  }}
                />
                )}
              </FormControl>
            </Grid>
            {/* CMO Short Name */}
            <Grid item xs={12} sm={6}>
              <FormControl className={classes?.formField}>                <Typography component="label" htmlFor="cmoShortName">
                CMO Short Name {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
                {(readOnly || hideStepActions || existingCmoDetailsReadOnly) ? (
                  <Typography>
                    {dataSource?.cmoShortFormName || formData?.cmoShortName || formData?.cmoDetails?.organizationDetails?.orgShortName || formData?.cmoDetails?.organizationDetails?.cmoShortFormName || stepData?.cmoShortName || "N/A"}
                  </Typography>
                ) : (<TextField
                  id="cmoShortName"
                  name="cmoShortName"
                  value={stepData.cmoShortName}
                  onChange={handleFieldChange}
                  onBlur={(e) => {
                    const { value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      handleFieldChange({ target: { name: "cmoShortName", value: value.trim() } });
                    }
                  }}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  required
                  error={submitted && Boolean(errors.cmoShortName)}
                  helperText={submitted ? errors.cmoShortName : ""}
                  InputProps={{ style: commonInlineStyles.input }}
                  sx={inputFieldStyles}
                  inputProps={{
                    ...commonInlineStyles.inputProps,
                    maxLength: 20,
                  }}
                  style={commonInlineStyles.input}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^a-zA-Z0-9\-_]/g, "")
                      .replace(/(-{2,}|_{2,})/g, (match) => match[0]);
                  }}
                />
                )}
              </FormControl>
            </Grid>
            {/* Street Address 1 */}
            <Grid item xs={12} sm={6} className="multiple-line-text">
              <FormControl className={classes?.formField}>                <Typography component="label" htmlFor="streetAddress1">
                Street Address 1 {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>{" "}
                {readOnly || existingCmoDetailsReadOnly ? (
                  <Typography>
                    {dataSource?.streetAddress1 || formData?.streetAddress1 || formData?.cmoDetails?.organizationDetails?.streetAddress1 || stepData?.streetAddress1 || "N/A"}
                  </Typography>
                ) : (
                  <TextField
                    id="streetAddress1"
                    name="streetAddress1"
                    value={stepData.streetAddress1}
                    onChange={handleFieldChange}
                    onBlur={(e) => {
                      const { value } = e.target;
                      // Trim leading and trailing spaces for string values
                      if (typeof value === "string" && value.trim() !== value) {
                        handleFieldChange({ target: { name: "streetAddress1", value: value.trim() } });
                      }
                    }}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    maxRows={10}
                    error={submitted && Boolean(errors.streetAddress1)}
                    helperText={submitted ? errors.streetAddress1 : ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={{
                      ...commonInlineStyles.inputProps,
                      maxLength: 255,
                    }}
                    style={commonInlineStyles.input}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^a-zA-ZÀ-ÿ0-9\s&.,\-/]/g, "")
                        .replace(/(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g, (match) => match[0]);
                    }}
                  />
                )}
              </FormControl>
            </Grid>
            {/* Street Address 2 */}
            <Grid item xs={12} sm={6} className="multiple-line-text">
              <FormControl className={classes?.formField}>                <Typography component="label" htmlFor="streetAddress2">
                Street Address 2 {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>{" "}
                {readOnly || existingCmoDetailsReadOnly ? (
                  <Typography>
                    {dataSource?.streetAddress2 || formData?.streetAddress2 || formData?.cmoDetails?.organizationDetails?.streetAddress2 || stepData?.streetAddress2 || "N/A"}
                  </Typography>
                ) : (<TextField
                  id="streetAddress2"
                  name="streetAddress2"
                  value={stepData.streetAddress2}
                  onChange={handleFieldChange}
                  onBlur={(e) => {
                    const { value } = e.target;
                    // Trim leading and trailing spaces for string values
                    if (typeof value === "string" && value.trim() !== value) {
                      handleFieldChange({ target: { name: "streetAddress2", value: value.trim() } });
                    }
                  }}
                  variant="outlined"
                  placeholder="Enter text here"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={10}
                  required
                  error={submitted && Boolean(errors.streetAddress2)}
                  helperText={submitted ? errors.streetAddress2 : ""}
                  InputProps={{ style: commonInlineStyles.input }}
                  sx={inputFieldStyles}
                  inputProps={{
                    ...commonInlineStyles.inputProps,
                    maxLength: 255,
                  }}
                  style={commonInlineStyles.input}
                  onInput={(e) => {
                    e.target.value = e.target.value
                      .replace(/[^a-zA-ZÀ-ÿ0-9\s&.,\-/]/g, "")
                      .replace(/(\s{2,}|&{2,}|,-{2,}|,{2,}|\.{2,}|\/{2,}|'{2,}|\({2,}|\){2,})/g, (match) => match[0]);
                  }}
                />
                )}
              </FormControl>
            </Grid>

            {/* Country */}
            <Grid item xs={12} sm={6}>
              <FormControl className={classes?.formField}>                <Typography component="label" htmlFor="country">
                Country {!readOnly && <span style={{ color: "red" }}>*</span>}
              </Typography>
                {readOnly || existingCmoDetailsReadOnly ? (
                  <Typography>
                    {dataSource?.country || location.state?.cmoDetails?.organizationDetails?.country || formData?.country || formData?.cmoDetails?.organizationDetails?.country || stepData?.country || "N/A"}
                  </Typography>
                ) : (
                  <Select
                    select
                    id="country"
                    name="country"
                    value={stepData.country || ""}
                    onChange={handleFieldChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={submitted && Boolean(errors.country)}
                    helperText={submitted ? errors.country : ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={commonInlineStyles.inputProps}
                    style={commonInlineStyles.input}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select from the list
                    </MenuItem>
                    {Country.getAllCountries().map((country) => (
                      <MenuItem key={country.isoCode} value={country.isoCode}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Grid>
            {/* State */}
            <Grid item xs={12} sm={4}>
              <FormControl className={classes?.formField}>
                <Typography component="label" htmlFor="state">
                  State {!readOnly && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {readOnly || existingCmoDetailsReadOnly ? (
                  <Typography>
                    {dataSource?.state || location.state?.cmoDetails?.organizationDetails?.state || formData?.state || formData?.cmoDetails?.organizationDetails?.state || stepData?.state || "N/A"}
                  </Typography>
                ) : (
                  <Select
                    select
                    id="state"
                    name="state"
                    value={stepData.state}
                    onChange={handleFieldChange}
                    variant="outlined"
                    fullWidth
                    required
                    disabled={!stepData.country}
                    error={submitted && Boolean(errors.state)}
                    helperText={submitted ? errors.state : ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={commonInlineStyles.inputProps}
                    style={commonInlineStyles.input}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select from the list
                    </MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
            </Grid>
            {/* City */}
            <Grid item xs={12} sm={4}>
              <FormControl className={classes?.formField}>
                <Typography component="label" htmlFor="city">
                  City {!readOnly && <span style={{ color: "red" }}>*</span>}
                </Typography>{" "}
                {readOnly || existingCmoDetailsReadOnly ? (
                  <Typography>
                    {dataSource?.city || formData?.city || formData?.cmoDetails?.organizationDetails?.city || stepData?.city || "N/A"}
                  </Typography>
                ) : (
                  <Select
                    select
                    id="city"
                    name="city"
                    value={stepData.city || ""}
                    onChange={handleFieldChange}
                    variant="outlined"
                    fullWidth
                    required
                    disabled={!stepData.state}
                    error={submitted && Boolean(errors.city)}
                    helperText={submitted ? errors.city : ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={commonInlineStyles.inputProps}
                    style={commonInlineStyles.input}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select from the list
                    </MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city.name} value={city.name}>
                        {city.name}
                      </MenuItem>
                    ))}
                    {/* If city isn't in the list but we have a value, add it as an option */}
                    {stepData.city &&
                      !cities.some((c) => c.name === stepData.city) && (
                        <MenuItem key={stepData.city} value={stepData.city}>
                          {stepData.city}
                        </MenuItem>
                      )}
                  </Select>
                )}
              </FormControl>
            </Grid>
            {/* Post Code */}
            <Grid item xs={12} sm={4}>
              <FormControl className={classes?.formField}>
                <Typography component="label" htmlFor="postCode">
                  Post Code {!readOnly && <span style={{ color: "red" }}>*</span>}
                </Typography>{" "}
                {readOnly || existingCmoDetailsReadOnly ? (
                  <Typography>
                    {dataSource?.postCode || formData?.postCode || formData?.cmoDetails?.organizationDetails?.postCode || stepData?.postCode || "N/A"}
                  </Typography>
                ) : (
                  <TextField
                    id="postCode"
                    name="postCode"
                    value={stepData.postCode}
                    onChange={handlePostalCodeChange}
                    variant="outlined"
                    placeholder="Enter text here"
                    fullWidth
                    required
                    error={Boolean(errors.postCode) || Boolean(postalCodeError)}
                    helperText={errors.postCode || postalCodeError || ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={{
                      ...commonInlineStyles.inputProps,
                      maxLength: 12,
                    }}
                    style={commonInlineStyles.input}
                    onInput={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^a-zA-Z0-9\s-]/g, "")
                        .replace(/(\s{2,}|-{2,})/g, "");
                    }}
                  />
                )}
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl className={classes?.formField}>
                <Typography component="label" htmlFor="status">
                  Status {!readOnly && <span style={{ color: "red" }}>*</span>}
                </Typography>
                {readOnly ? (
                  <Typography>
                    {formData?.status || formData?.cmoDetails?.organizationDetails?.status || stepData?.status || "N/A"}
                  </Typography>
                ) : (
                  <TextField
                    select
                    id="status"
                    name="status"
                    value={formData?.status || stepData.status || "Active"}
                    onChange={handleFieldChange}
                    variant="outlined"
                    fullWidth
                    required
                    error={submitted && Boolean(errors.status)}
                    helperText={submitted ? errors.status : ""}
                    InputProps={{ style: commonInlineStyles.input }}
                    sx={inputFieldStyles}
                    inputProps={commonInlineStyles.inputProps}
                    style={commonInlineStyles.input}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </FormControl>
            </Grid>
            {existingCmoDetailsReadOnly && location.state?.cmoDetails?.organizationDetails?.organizationCode && <Grid item xs={12} sm={4}>
              <FormControl className={classes?.formField}>
                <Typography component="label" htmlFor="postCode">
                Organization Code
                </Typography>{" "}
                  <Typography>
                    {location.state?.cmoDetails?.organizationDetails?.organizationCode || "N/A"}
                  </Typography>
                </FormControl>
              </Grid>}

          </Grid>
          {<Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />}

          {!hideAdminDetails && <AdminDetails
            formData={formData}
            updateStepData={updateStepData}
            handleNext={handleNext}
            onValidationChange={onValidationChange}
            onFieldChange={onFieldChange}
            readOnly={readOnly}
            stepData={stepData}
            submitted={submitted}
            errors={errors}
            handleFieldChange={handleFieldChange}
            handlePhoneCodeChange={handlePhoneCodeChange}
            handlePhoneNumberChange={handlePhoneNumberChange}
            handlePostalCodeChange={handlePostalCodeChange}
            handlePhoneKeyDown={handlePhoneKeyDown}
            classes={classes}
            statusOptions={statusOptions}
            states={states}
            cities={cities}
            Country={Country}
            commonInlineStyles={commonInlineStyles}
            inputFieldStyles={inputFieldStyles}
            dataSource={dataSource}
          />}

          {
            (!hideStepActions) && (
              <div
                style={{ display: "flex", justifyContent: "center" }}
                className="stepper-navigation-btns"
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNextClick}
                  className={classes.button}
                  endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIosIcon style={{ fontSize: 15 }} />}
                  disabled={loading}
                >            {loading ? "Saving..." : "Save & Next"}
                </Button>
              </div>
            )
          }
        </form>
        {readOnly && <Divider style={{ margin: "80px 00px 0px 00px", backgroundColor: '#707070', height: 1, }} />}

      </div>
    </div>
  );
};

export default CMODetails;


export const AdminDetails = (props) => {
  const { formData, updateStepData, handleNext, onValidationChange = () => { }, readOnly = false, stepData, submitted, errors, handleFieldChange, handlePhoneCodeChange, handlePhoneNumberChange, handlePostalCodeChange, handlePhoneKeyDown, classes, statusOptions, states, cities, Country, commonInlineStyles, inputFieldStyles, dataSource } = props;
  
  // Get updated values from sessionStorage for readOnly mode
  const cmoDetailsResponse = readOnly ? JSON.parse(sessionStorage.getItem("cmoDetailsResponse") || "{}") : {};
  // Custom handler for email field to ensure it can be cleared
  const handleEmailChange = (e) => {
    const { value } = e.target;
    // Use the standard handleFieldChange function but ensure we pass the exact value
    handleFieldChange({
      target: {
        name: "adminEmail",
        value: value
      }
    });
  };

  // Add a specific handler for email input keydown to detect backspace and delete
  const handleEmailKeyDown = (e) => {
    // Check if the key pressed is backspace or delete and all content is selected
    if ((e.key === 'Backspace' || e.key === 'Delete') &&
      (e.target.selectionStart === 0 && e.target.selectionEnd === e.target.value.length)) {
      // Clear the email field
      handleFieldChange({
        target: {
          name: "adminEmail",
          value: ""
        }
      });
    }
  };

  return <>
    <Typography
      variant="h6"
      style={{ margin: "32px 0 16px 0", fontWeight: 600 }}
    >
      Admin Details
    </Typography>
    <Grid className="two-col-forms-grid" container spacing={3}>
      <Grid item xs={12} sm={4}>
        <FormControl className={classes?.formField}>
          <Typography component="label" htmlFor="adminFullName">
            Full Name {!readOnly && <span style={{ color: "red" }}>*</span>}
          </Typography>{" "}                {readOnly ? (
            <Typography>
              {dataSource?.fullName || cmoDetailsResponse?.fullName || formData?.fullName || formData?.adminDetails?.fullName || formData?.cmoDetails?.organizationDetails?.fullName || stepData?.adminFullName || "N/A"}
            </Typography>
          ) : (
            <TextField
              id="adminFullName"
              name="adminFullName"
              value={stepData?.adminFullName}
              onChange={handleFieldChange}
              onBlur={(e) => {
                const { value } = e.target;
                // Trim leading and trailing spaces for string values
                if (typeof value === "string" && value.trim() !== value) {
                  handleFieldChange({ target: { name: "adminFullName", value: value.trim() } });
                }
              }}
              variant="outlined"
              placeholder="Enter text here"
              fullWidth
              required
              error={submitted && Boolean(errors?.adminFullName)}
              helperText={submitted ? errors?.adminFullName : ""}
              InputProps={{ style: commonInlineStyles?.input }}
              sx={inputFieldStyles}
              inputProps={{
                ...commonInlineStyles?.inputProps,
                maxLength: 30,
              }}
              style={commonInlineStyles?.input} onInput={(e) => {
                e.target.value = e.target.value
                  .replace(/[^a-zA-Z\s]/g, "")
                  .replace(/(\s{2,})/g, (match) => match[0]);
              }}
            />
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl className={classes?.formField}>
          <Typography component="label" htmlFor="adminEmail">
            Email Address {!readOnly && <span style={{ color: "red" }}>*</span>}
          </Typography>{" "}
          {readOnly ? (
            <Typography>
              {dataSource?.emailAddress || cmoDetailsResponse?.email || formData?.emailAddress || formData?.adminDetails?.email || formData?.adminDetails?.emailAddress || formData?.cmoDetails?.organizationDetails?.emailAddress || stepData?.adminEmail || "N/A"}
            </Typography>
          ) : (
            <TextField
              id="adminEmail"
              name="adminEmail"
              value={stepData?.adminEmail || ""}
              onChange={handleEmailChange}  
              onBlur={(e) => {
                const { value } = e.target;
                // Trim leading and trailing spaces for string values
                if (typeof value === "string" && value.trim() !== value) {
                  handleEmailChange({ target: { name: "adminEmail", value: value.trim() } });
                }
              }}
              onKeyDown={handleEmailKeyDown}
              variant="outlined"
              placeholder="Enter text here"
              fullWidth
              required
              error={submitted && Boolean(errors?.adminEmail)}
              helperText={submitted ? errors?.adminEmail : ""}
              InputProps={{ style: commonInlineStyles?.input }}
              sx={inputFieldStyles}
              inputProps={{
                ...commonInlineStyles?.inputProps,
                maxLength: 320,
              }}
              style={commonInlineStyles?.input} onInput={(e) => {
                e.target.value = e.target.value
                  .replace(/[^a-zA-Z0-9._%+\-@]/g, "")
                  .replace(/\.{2,}/g, ".");
              }}
            />
          )}
        </FormControl>
      </Grid>{" "}
      <Grid item xs={12} sm={4}>
        <FormControl className={classes?.formField}>
          <Typography
            component="label"
            htmlFor="adminPhone"
            style={{ marginBottom: "8px" }}
          >
            Phone {!readOnly && <span style={{ color: "red" }}>*</span>}
          </Typography>                {readOnly ? (
            <Typography>
              {dataSource?.phoneNumber || cmoDetailsResponse?.phoneNumber || formData?.phoneNumber || formData?.adminDetails?.phone || formData?.adminDetails?.phoneNumber || formData?.cmoDetails?.organizationDetails?.phoneNumber || stepData?.adminPhoneCode || "N/A"}
            </Typography>
          ) : (
            <PhoneInput
              country={(stepData?.adminPhoneCode && stepData?.adminPhoneCode !== "") ? stepData?.adminPhoneCode.toLowerCase() : "us"}
              value={stepData?.adminPhoneCode || ""}
              onChange={handlePhoneCodeChange}
              onKeyDown={handlePhoneKeyDown}
              enableSearch={true}
              defaultErrorMessage="Invalid phone number"
              inputStyle={{
                width: "100%",
                height: "2.5rem",
                borderColor: submitted && Boolean(errors.adminPhone) ? "#f44336" : "#0000008a",
              }}
              containerStyle={{ margin: "0" }}
              dropdownStyle={{ borderColor: "#0000008a" }}
              countryCodeEditable={false}
              isValid={!submitted || !errors.adminPhone}
            />
          )}
          {submitted && errors?.adminPhone && (
            <Typography
              variant="caption"
              style={{
                color: "#f44336",
                fontSize: "14px",
                display: "block",
                marginTop: "4px",
              }}
            >
              {errors?.adminPhone}
            </Typography>
          )}
        </FormControl>
      </Grid>
    </Grid>
  </>;
};